import React, { useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';

const menuItems = [
  {
    title: 'Company',
    items: [
      { name: 'About Data Speedway', path: '/about/company' },
      { name: 'Our Team', path: '/about/team' },
      { name: 'Certifications', path: '/certifications' },
      { name: 'Contact Support', path: '/contact/support' },
    ],
  },
  {
    title: 'Services',
    items: [
      { name: 'POS & Systems', path: '/possys' },
      { name: 'Network Hardware', path: '/networks' },
      { name: 'Wireless & Site Surveys', path: '/wifi' },
      { name: 'Structured Cabling', path: '/ethernet' },
      { name: 'AI Solutions', path: '/aiagents' },
    ],
  },
  {
    title: 'Legal',
    items: [
      { name: 'Privacy Policy', path: '/privacy-policy' },
      { name: 'Terms of Service', path: '/terms-of-service' },
    ]
  }
];

function Copyright() {
  return (
    <Typography variant="body2" align="center" color="text.secondary">
      {'© '}
      <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
        Data Speedway
      </RouterLink>{' '}
      {new Date().getFullYear()}
      {'. All rights reserved.'}
    </Typography>
  );
}

export default function PortalFooter() {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState('');

  const handleOpenDialog = (content) => {
    setDialogContent(content);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogContent('');
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        pt: 6,
        pb: 4,
        mt: 'auto',
        width: '100%',
        borderTop: '1px solid #e0e0e0'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="space-between">

          {menuItems.map((menu, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                {menu.title}
              </Typography>
              <List dense>
                {menu.items.map((item, subIndex) => (
                  <ListItem
                    key={subIndex}
                    disableGutters
                    sx={{ pb: 0, pt: 0 }}
                  >
                    <Button
                      onClick={() => handleOpenDialog(item.name)}
                      sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        justifyContent: 'flex-start',
                        padding: 0,
                        minWidth: 0,
                        '&:hover': { color: '#FACD00', backgroundColor: 'transparent' }
                      }}
                      disableRipple
                    >
                      {item.name}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 'bold' }}>
              Connect
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Nationwide IT Services<br />
              Serving businesses across<br />
              North America
            </Typography>
            <Typography variant="body2" color="text.secondary">
              24/7 Support Available<br />
              Email: info@dataspeedway.com
            </Typography>
          </Grid>

        </Grid>

        <Box mt={5}>
          <Copyright />
        </Box>

      </Container>

      <Dialog fullScreen open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseDialog}
            aria-label="close"
          >
            <CloseIcon fontSize='large' />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, minHeight: '80vh', alignItems: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: 800, p: 2 }}>
              <Typography variant="h3" gutterBottom fontWeight={800} align="center">
                {dialogContent}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mt: 4, lineHeight: 1.8 }}>
                {dialogContent === 'About Data Speedway' && <p>Data Speedway is a premier provider of on-site IT services, dedicated to accelerating digital transformation for businesses nationwide. We specialize in deploying robust network infrastructure, seamless POS systems, and cutting-edge cloud solutions. Our team of certified technicians and engineers ensures that your technology travels at the speed of data, enabling you to focus on growing your business while we handle the complexities of connectivity and infrastructure.</p>}

                {dialogContent === 'Our Mission' && <p>Our mission is to empower businesses with high-velocity, reliable, and secure technology solutions. We believe that a strong digital foundation is the key to modern success. By combining technical expertise with a customer-centric approach, we aim to eliminate downtime, optimize performance, and drive efficiency across every location we serve. We are committed to setting the standard for excellence in field services and IT project management.</p>}

                {dialogContent === 'Contact Support' && <p>Need assistance? Our support team is ready to help. Whether you're facing a critical network outage, need a quick POS configuration change, or have questions about a future project, we are just a call or click away. Reach out to our 24/7 helpdesk for immediate troubleshooting or schedule a consultation with our project managers. Your operational continuity is our top priority.</p>}

                {dialogContent === 'Careers' && <p>Join the Data Speedway team! We are looking for driven technicians, network engineers, and project managers who are passionate about technology. We offer a dynamic work environment, opportunities for continuous learning, and the chance to work on exciting projects with leading brands. If you're ready to fast-track your career in IT field services, check out our open positions and apply today.</p>}

                {dialogContent === 'POS Implementation' && <p>Our POS Implementation service ensures a flawless launch for your retail or restaurant operations. As certified installers for major platforms like Toast, we handle everything from physical mounting and cabling to software configuration and menu programming. We don't just plug it in; we optimize the system for your specific workflow, conduct staff training, and provide go-live support to ensure day one is a success.</p>}

                {dialogContent === 'Network Hardware' && <p>Building a reliable network requires more than just buying routers; it requires expert architecture and installation. We supply, configure, and install enterprise-grade networking hardware including firewalls, switches, and access points. Our team ensures all equipment is rack-mounted professionally, cabling is managed neatly, and configurations adhere to strict security compliance standards (PCI-DSS, SOC2).</p>}

                {dialogContent === 'Wireless Services' && <p>Dead zones can kill productivity. Our Wireless Services cover the full lifecycle of Wi-Fi deployment. We conduct predictive heatmaps to plan coverage, perform active site surveys to validate signal strength, and install indoor/outdoor Access Points for seamless roaming. Whether it's a warehouse, a campus, or a dense office environment, we ensure your wireless network is fast, secure, and ubiquitous.</p>}

                {dialogContent === 'Structured Cabling' && <p>The backbone of any network is its cabling. Our Structured Cabling services provide certified installation of Cat6, Cat6A, and Fiber Optic lines. We handle new construction rough-ins, office remodels, and data center build-outs. From termination and testing to labeling and rack organization, we ensure your physical infrastructure is neat, organized, and capable of supporting high-speed data transmission required by modern devices.</p>}

                {dialogContent === 'Cloud Infrastructure' && <p>Modernize your operations with our Google Cloud Platform services. We help you migrate, deploy, and manage workloads in the cloud. From setting up autoscaling Compute Engine instances and serverless Cloud Run applications to managing secure Cloud Storage and Cloud SQL databases, we provide the architectural expertise to leverage the full power of the cloud for scalability and innovation.</p>}

                {dialogContent === 'Privacy Policy' && <p>Your privacy is paramount. Data Speedway is committed to protecting the data of our clients and their customers. We adhere to strict data protection protocols and do not share your information with third parties without consent. Our transparent policies ensure you know exactly how your data is collected, stored, and used to provide our services.</p>}

                {dialogContent === 'Terms of Service' && <p>By using Data Speedway's services, you agree to our standard terms covering service level agreements (SLAs), liability, and payment terms. We believe in clear, fair contracts that protect both parties and ensure a successful partnership. Please review our full terms document for detailed information on warranties and service expectations.</p>}

              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
